import classNames from "classnames";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";

import {
  useClaimsFiltersSchema,
  useSignalEventOccurrencesFiltersSchema,
} from "shared/hooks";
import { getAttributeLabel } from "shared/utils";

import {
  ANY_ALL_INDEX,
  getOperatorToLabel,
} from "features/ui/Filters/constants";
import FilterQueryPresentation from "features/ui/Filters/FilterBuilder/FilterQueryPresentation";
import { filterBuilderQueryToFilterBuilderState } from "features/ui/Filters/FilterBuilder/utils";
import {
  FilterDiffData,
  FilterOperator,
  FilterOverviewFormat,
} from "features/ui/Filters/types";

import { decodeOccursFilterAndOptions } from "./utils";

interface Props {
  operator: FilterOperator;
  value: string;
  baseEntityText?: string;
  occursBeforeNow?: boolean;
  format: FilterOverviewFormat;
  diff?: FilterDiffData;
  fieldName?: string;
}

const windowDirectionToLabel = (t: TFunction, windowDirection: string) =>
  t(`occurs.direction.${windowDirection}`);

const OccursFilterLabel = ({
  operator,
  value,
  baseEntityText: baseEntityTextProp,
  occursBeforeNow = false,
  format,
  diff,
  fieldName,
}: Props) => {
  // `getOperatorToLabel` resolves bare keys, so `t` must stay bound to `filters`.
  const { t } = useTranslation("filters");
  const { filters, windowSize, windowDirection, dateField } =
    decodeOccursFilterAndOptions(value);

  const signalEventSchema = useSignalEventOccurrencesFiltersSchema();
  const claimsSchema = useClaimsFiltersSchema();

  const baseEntityText = baseEntityTextProp ?? t("occurs.base_object");

  // determine schema based on fieldName
  const schema =
    fieldName === "relatedClaims" ? claimsSchema : signalEventSchema;

  const dateFieldLabel = dateField
    ? getAttributeLabel(dateField)
    : t("occurs.date");

  // The window unit is fixed to DEFAULT_WINDOW_TYPE ("days"), so it lives in the translation key.
  const timeWindowText = (
    <span className="font-normal">
      <Trans
        t={t}
        i18nKey={
          occursBeforeNow
            ? "filters:occurs.label.before_now"
            : "filters:occurs.label.relative"
        }
        count={Number(windowSize)}
        values={{
          operator: getOperatorToLabel(t)[operator],
          direction: windowDirectionToLabel(t, windowDirection as string),
          entity: baseEntityText,
          dateField: dateFieldLabel,
        }}
        components={{ strong: <strong /> }}
      />
    </span>
  );

  // todo miha: we could handle OCCURS filter diff more properly
  //  meaning we would also need to handle this separately in getUpdatedDeletedAttributes
  const applyValueDiffStyle =
    diff?.changes[diff.level]?.[diff.groupIndex]?.[diff.rowIndex]?.values ??
    false;

  const applyMarkAllDiffStyle =
    diff?.changes[diff.level]?.[diff.groupIndex]?.[ANY_ALL_INDEX]?.markAll ??
    false;

  return (
    <div className="text-left">
      <span
        className={classNames(
          "font-normal",
          applyMarkAllDiffStyle ? diff?.bgColor : undefined
        )}
      >
        {t("occurs.defined_by")}
      </span>
      <div
        className={classNames(
          format === "label-inline" ? "ml-1" : "mt-1",
          applyValueDiffStyle ? diff?.bgColor : undefined
        )}
      >
        <FilterQueryPresentation
          filterState={filterBuilderQueryToFilterBuilderState(filters)}
          tableSchema={schema}
          baseEntityText={baseEntityText}
          occursBeforeNow={occursBeforeNow}
        />
      </div>
      <span
        className={classNames(applyValueDiffStyle ? diff?.bgColor : undefined)}
      >
        {timeWindowText}
      </span>
    </div>
  );
};

export default OccursFilterLabel;
